import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  Webhook,
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantWebhookLogs, getMerchantWebhookLogsCount } from "@/shared/api/services/merchantService";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const statusConfig = {
  received: { icon: Clock, color: "bg-blue-100 text-blue-700" },
  validated: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  processed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
  invalid_signature: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  duplicate: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700" },
};

const eventTranslationKeys = {
  "payment.failed": "paymentFailed",
  "payment.succeeded": "paymentSucceeded",
  "withdrawal.succeeded": "withdrawalSucceeded",
  "withdrawal.failed": "withdrawalFailed",
};

// Helper function to translate status values
const translateStatus = (status, t) => {
  const statusMap = {
    received: "received",
    validated: "validated",
    processed: "processed",
    failed: "failed",
    invalid_signature: "invalidSignature",
    duplicate: "duplicate",
  };
  const translationKey = statusMap[status] || status;
  return t(translationKey) || status;
};

const normalizeEventValue = (eventValue = "") => eventValue.toLowerCase().trim().replace(/[\s_]+/g, ".");

const getEventLabel = (eventValue, t) => {
  const normalizedEvent = normalizeEventValue(eventValue);
  return t(eventTranslationKeys[normalizedEvent] || normalizedEvent);
};

export default function MerchantWebhooks() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const { data: totalCount = 0 } = useQuery({
    queryKey: ["merchant-webhook-logs-count"],
    queryFn: () => getMerchantWebhookLogsCount(),
  });

  const { data: rawLogs = [], isLoading } = useQuery({
    queryKey: ["merchant-webhook-logs", page],
    queryFn: () => getMerchantWebhookLogs(page + 1, pageSize),
    keepPreviousData: true,
  });

  const filteredWebhooks = rawLogs.filter((log) => {
    if (search) {
      const term = search.toLowerCase();
      const matches =
        log.id?.toLowerCase().includes(term) ||
        log.paymentId?.toLowerCase().includes(term) ||
        log.transactionId?.toLowerCase().includes(term);
      if (!matches) return false;
    }
    if (directionFilter !== "all" && log.direction !== directionFilter) return false;
    if (eventFilter !== "all" && normalizeEventValue(log.event) !== normalizeEventValue(eventFilter)) return false;
    return true;
  });

  const totalPages = totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize);

  const formatJson = (value) => {
    if (!value) return "{}";
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof value === "string" ? value : JSON.stringify(value, null, 2);
    }
  };

  useEffect(() => {
    if (totalPages === 0 && page !== 0) {
      setPage(0);
      return;
    }
    if (totalPages > 0 && page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  return (
    <MerchantLayout currentPage="MerchantWebhooks">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("webhookLogs")}</h1>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={`${t("searchByWebhookId")} / ${t("paymentId") || "Payment ID"}`}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10"
            />
          </div>
          <Select value={directionFilter} onValueChange={(v) => { setDirectionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("direction")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allDirection")}</SelectItem>
              <SelectItem value="inbound">{t("inbound")}</SelectItem>
              <SelectItem value="outbound">{t("outbound")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("event")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              <SelectItem value="payment.failed">{t("paymentFailed")}</SelectItem>
              <SelectItem value="payment.succeeded">{t("paymentSucceeded")}</SelectItem>
              <SelectItem value="withdrawal.succeeded">{t("withdrawalSucceeded")}</SelectItem>
              <SelectItem value="withdrawal.failed">{t("withdrawalFailed")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("webhookId")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("direction")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("event")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("response")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("date")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td colSpan={6} className="px-4 py-4">
                        <Skeleton height={16} />
                      </td>
                    </tr>
                  ))
                ) : filteredWebhooks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-slate-500">
                      <Webhook className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>{t("noWebhookLogs")}</p>
                    </td>
                  </tr>
                ) : (
                  filteredWebhooks.map((log, index) => (
                      <motion.tr
                        key={log.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="border-b border-slate-50 hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <p className="font-mono text-sm text-slate-900">{log.id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={`gap-1 ${log.direction === "inbound" ? "border-blue-200 text-blue-700" : "border-emerald-200 text-emerald-700"}`}
                          >
                            {log.direction === "inbound" ? (
                              <ArrowDownLeft className="h-3 w-3" />
                            ) : (
                              <ArrowUpRight className="h-3 w-3" />
                            )}
                            {t(log.direction)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">
                            {getEventLabel(log.event, t)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${
                            log.httpStatus >= 200 && log.httpStatus < 300 ? "text-emerald-600" : "text-red-600"
                          }`}>
                            {log.httpStatus || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {log.createdAt ? format(new Date(log.createdAt), "MMM d, HH:mm:ss") : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </motion.tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {totalCount === 0 ? 0 : page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalCount)} {t("of")} {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("webhookDetails")}</SheetTitle>
          </SheetHeader>

          {selectedLog && (
            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">{t("webhookId")}</p>
                    <p className="font-mono text-sm">{selectedLog.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("transactionId")}</p>
                    <p className="font-mono text-sm">{selectedLog.transactionId || selectedLog.paymentId || t("nA")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("direction")}</p>
                    <Badge variant="outline">{t(selectedLog.direction)}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("status")}</p>
                    <Badge className={statusConfig[selectedLog.status]?.color || "bg-slate-100"}>
                      {translateStatus(selectedLog.status, t)}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("url")}</p>
                  <p className="font-mono text-sm bg-slate-50 p-2 rounded break-all">
                    {selectedLog.url || t("nA")}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestHeaders")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                    {"{}"}
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestPayload")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[200px]">
                    {formatJson(selectedLog.requestPayload)}
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("responseStatus")}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{t("status")}: {selectedLog.httpStatus || t("nA")}</Badge>
                  </div>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[200px]">
                    {selectedLog.responseBody || t("noResponseBody")}
                  </pre>
                </div>

                {selectedLog.errorMessage && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800">{t("error")}</p>
                    <p className="text-sm text-red-700">{selectedLog.errorMessage}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

    </MerchantLayout>
  );
}